import { useState, type FormEvent } from 'react'
import clsx from 'clsx'
import { fetchTraceJourney, grafanaExplorePath, searchTraces } from './useAdminApi'
import type { TraceHop, TraceJourney, TraceLogLine, TracePacket } from './types'
import { Banner, SectionHeader } from './ui'

type View = 'journey' | 'logs' | 'grafana'

function formatIst(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const clock = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(d)
  return `${clock} IST`
}

function istLocalToIso(value: string): string {
  if (!value) return ''
  return `${value.length === 16 ? `${value}:00` : value}+05:30`
}

function logsForHop(logs: TraceLogLine[], hop: TraceHop | undefined): TraceLogLine[] {
  if (!hop) return logs
  const matched = logs.filter((line) => line.service === hop.service || line.event === hop.event)
  return matched.length > 0 ? matched : logs
}

export function TracesSection({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [view, setView] = useState<View>('journey')
  const [traceId, setTraceId] = useState('')
  const [alertId, setAlertId] = useState('')
  const [tripId, setTripId] = useState('')
  const [vehicleId, setVehicleId] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [packets, setPackets] = useState<TracePacket[]>([])
  const [journey, setJourney] = useState<TraceJourney | null>(null)
  const [hopIndex, setHopIndex] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)

  if (!isSuperAdmin) {
    return (
      <p className="text-sm text-slate-500">
        Packet traces are limited to the platform's super administrator.
      </p>
    )
  }

  async function openTrace(id: string) {
    const body = await fetchTraceJourney(id)
    setJourney(body)
    setHopIndex(0)
    setView('journey')
  }

  async function onSearch(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setLoading(true)
    setSearched(true)
    try {
      const found = await searchTraces({
        traceId, alertId, tripId, vehicleId,
        start: istLocalToIso(start),
        end: istLocalToIso(end),
      })
      setPackets(found)
      const id = traceId.trim() || found[0]?.trace_id || ''
      if (id) await openTrace(id)
      else setJourney(null)
    } catch (err) {
      setJourney(null)
      setPackets([])
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const hop = journey?.hops[hopIndex]
  const hopLogs = logsForHop(journey?.logs ?? [], hop)

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Packet traces"
        subtitle="One MQTT packet, from the bridge through every stage that touched it. Logs and spans are kept 30 days. Times are IST."
      />

      <form onSubmit={(event) => void onSearch(event)} className="grid gap-2 md:grid-cols-3">
        <Field label="Trace id" value={traceId} onChange={setTraceId} placeholder="32 hex characters" />
        <Field label="Alert id" value={alertId} onChange={setAlertId} placeholder="uuid" />
        <Field label="Trip id" value={tripId} onChange={setTripId} placeholder="trip id" />
        <Field label="Vehicle" value={vehicleId} onChange={setVehicleId} placeholder="registration" />
        <label className="block text-xs text-slate-500">
          From (IST)
          <input
            type="datetime-local"
            value={start}
            onChange={(event) => setStart(event.target.value)}
            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-800"
          />
        </label>
        <label className="block text-xs text-slate-500">
          To (IST)
          <input
            type="datetime-local"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-800"
          />
        </label>
        <div className="md:col-span-3">
          <button
            type="submit"
            disabled={loading}
            className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>
      </form>

      {error ? <Banner tone="error">{error}</Banner> : null}

      <div className="flex gap-1 border-b border-slate-200">
        {(['journey', 'logs', 'grafana'] as View[]).map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setView(id)}
            className={clsx(
              'px-3 py-1.5 text-xs font-medium capitalize',
              view === id ? 'border-b-2 border-slate-900 text-slate-900' : 'text-slate-500',
            )}
          >
            {id}
          </button>
        ))}
      </div>

      {searched && packets.length > 0 ? (
        <ul className="flex gap-2 overflow-x-auto pb-1">
          {packets.map((packet) => (
            <li key={packet.trace_id}>
              <button
                type="button"
                onClick={() => void openTrace(packet.trace_id).catch((err: unknown) => {
                  setError(err instanceof Error ? err.message : 'Could not open that packet')
                })}
                className={clsx(
                  'min-w-[220px] rounded-md border px-2 py-1.5 text-left text-xs',
                  journey?.trace_id === packet.trace_id ? 'border-slate-900 bg-slate-50' : 'border-slate-200',
                )}
              >
                <span className="block font-medium text-slate-800">{packet.vehicle_id || packet.trace_id.slice(0, 8)}</span>
                <span className="block text-slate-500">{formatIst(packet.started_at)} · {packet.event}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {view === 'journey' ? (
        <Journey
          journey={journey}
          hopIndex={hopIndex}
          hopLogs={hopLogs}
          searched={searched}
          onHop={setHopIndex}
        />
      ) : null}

      {view === 'logs' ? (
        <LogTable lines={journey?.logs ?? []} empty={searched ? 'No log lines for this packet.' : 'Search for a packet to read its lines.'} />
      ) : null}

      {view === 'grafana' ? (
        <iframe
          title="Grafana"
          src={grafanaExplorePath(journey?.trace_id ?? '')}
          className="h-[70vh] w-full rounded-md border border-slate-200 bg-white"
        />
      ) : null}
    </div>
  )
}

function Field({
  label, value, onChange, placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder: string
}) {
  return (
    <label className="block text-xs text-slate-500">
      {label}
      <input
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 font-mono text-sm text-slate-800"
      />
    </label>
  )
}

function Journey({
  journey, hopIndex, hopLogs, searched, onHop,
}: {
  journey: TraceJourney | null
  hopIndex: number
  hopLogs: TraceLogLine[]
  searched: boolean
  onHop: (index: number) => void
}) {
  if (!journey) {
    return (
      <p className="text-sm text-slate-500">
        {searched ? 'No packet matched that search inside the 30-day store.' : 'Search by trace, alert, trip, or a vehicle and an IST window.'}
      </p>
    )
  }
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <ol className="space-y-2" aria-label="Packet journey">
        {journey.hops.length === 0 ? (
          <li className="text-sm text-slate-500">{journey.hop_error || 'No spans for this packet.'}</li>
        ) : journey.hops.map((hop, index) => (
          <li key={`${hop.service}-${hop.event}-${index}`}>
            <button
              type="button"
              onClick={() => onHop(index)}
              className={clsx(
                'w-full rounded-md border px-3 py-2 text-left',
                index === hopIndex ? 'border-slate-900 bg-slate-50' : 'border-slate-200',
                hop.status === 'error' && 'border-rose-300 bg-rose-50',
              )}
            >
              <span className="block text-sm font-medium text-slate-900">{hop.service}</span>
              <span className="block font-mono text-xs text-slate-600">{hop.event}</span>
              <span className="block text-xs text-slate-500">
                {formatIst(hop.started_at)} · {hop.duration_ms} ms
              </span>
            </button>
          </li>
        ))}
      </ol>
      <div className="space-y-3">
        <p className="font-mono text-xs text-slate-500">{journey.trace_id}</p>
        {journey.rows.error ? <Banner tone="error">{journey.rows.error}</Banner> : null}
        <Linked rows={journey.rows} />
        <LogTable lines={hopLogs} empty="This hop has no log line." />
      </div>
    </div>
  )
}

function Linked({ rows }: { rows: TraceJourney['rows'] }) {
  const bits = [
    ...rows.alerts.map((row) => `alert ${row.alert_id}`),
    ...rows.trips.map((row) => `trip ${row.trip_id}`),
    ...rows.round_trips.map((row) => `round trip ${row.round_trip_id}`),
  ]
  if (bits.length === 0) return null
  return <p className="text-xs text-slate-600">{bits.join(' · ')}</p>
}

function LogTable({ lines, empty }: { lines: TraceLogLine[]; empty: string }) {
  if (lines.length === 0) return <p className="text-sm text-slate-500">{empty}</p>
  return (
    <div className="overflow-auto rounded-md border border-slate-200">
      <table className="w-full text-left text-xs">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-2 py-1.5 font-medium">Time (IST)</th>
            <th className="px-2 py-1.5 font-medium">Level</th>
            <th className="px-2 py-1.5 font-medium">Service</th>
            <th className="px-2 py-1.5 font-medium">Event</th>
            <th className="px-2 py-1.5 font-medium">Message</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr key={`${line.timestamp}-${index}`} className={line.level === 'ERROR' ? 'bg-rose-50' : undefined}>
              <td className="whitespace-nowrap px-2 py-1.5 text-slate-600">{formatIst(line.timestamp)}</td>
              <td className="px-2 py-1.5">{line.level}</td>
              <td className="px-2 py-1.5">{line.service}</td>
              <td className="px-2 py-1.5 font-mono">{line.event}</td>
              <td className="px-2 py-1.5 text-slate-700">{line.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
